import React from 'react'
import { ProfileCircle } from 'iconsax-react'
import { useTheme } from '@mui/material'

import { User } from '../MergeContactBox'

import SearchResult from '../SearchResult'

import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import useDelayedUserSearch, {
  EXCLUDE_ME
} from '@SonarCX/hooks/useDelayedUserSearch/v2'
import useStyles from './styles'

interface MergeContactSearchProps {
  rootRef: React.MutableRefObject<HTMLDivElement>
  onSelect: (user: User) => void
  primaryContact: User
}

const MergeContactSearch: React.FC<MergeContactSearchProps> = ({
  rootRef,
  onSelect,
  primaryContact
}) => {
  const theme = useTheme()

  const { updateUsers } = useUserAPI()

  const anchorRef = React.useRef<HTMLDivElement>()
  const inputRef = React.useRef<HTMLInputElement>()
  const [searchVal, setSearchVal] = React.useState('')
  const [menuOpen, setMenuOpen] = React.useState(false)

  const { search, results } = useDelayedUserSearch(
    true,
    EXCLUDE_ME(() => primaryContact?.id)
  )
  React.useEffect(() => {
    updateUsers(results)
  }, [updateUsers, results])

  const classes = useStyles()

  // Show results when search is valid
  React.useEffect(() => {
    const showMenu = results.length > 0 && searchVal.length > 0
    setMenuOpen(showMenu)

    if (showMenu) {
      // Refocus input
      if (inputRef.current) {
        setTimeout(() => {
          inputRef.current.focus()
        }, 0)
      }
    }
  }, [results, searchVal])

  return (
    <>
      <div className={classes.avatarHolder}>
        <ProfileCircle size={theme.size.xxxl} variant='Bold' color='#8F8F8F' />
      </div>

      <div className={classes.findTitle}>Find a contact</div>

      <div
        ref={anchorRef}
        onClick={() => {
          if (inputRef.current) {
            inputRef.current.focus()
          }
        }}
        className={classes.inputArea}
      >
        <input
          ref={inputRef}
          autoFocus
          placeholder='Search for name, phone number, or email ...'
          value={searchVal}
          onChange={e => {
            // Update value and lookup users
            const val = e.target.value
            setSearchVal(val)
            search(val)
          }}
          className={classes.input}
        />
      </div>

      {menuOpen && (
        <div
          className={classes.resultsContainer}
          style={{
            top: `${
              (inputRef.current?.getBoundingClientRect()?.bottom ?? 0) -
              (rootRef.current?.getBoundingClientRect()?.y ?? 0) +
              8
            }px`,
            width: `${anchorRef.current?.getBoundingClientRect()?.width ?? 0}px`
          }}
        >
          <div id='merge-search-results' className={classes.results}>
            {results.map(u => {
              const user = u as User

              return (
                <div
                  key={user.id}
                  onClick={() => {
                    onSelect(user)

                    // Reset state
                    setSearchVal('')
                    setMenuOpen(false)
                    search('')
                  }}
                  className={classes.result}
                >
                  <SearchResult user={user} />
                </div>
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}

export default MergeContactSearch
